import { useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useCart } from "../context/CartContext"
import { ALL_PRODUCTS as PRODUCTS } from "../data/catalog"
import { QuoteButton } from "@/components/QuoteButton"
import { ProductExtras } from "@/components/ProductExtras"
import Seo from "@/components/Seo"
import { BULK_TIERS, discountRate, isDiscountable, lineTotal } from "@/lib/pricing"

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
})

export default function ProductDetail() {
  const { productId } = useParams()
  const product = PRODUCTS.find((entry) => entry.id === productId)
  const [selectedId, setSelectedId] = useState(product?.items[0]?.id ?? "")
  const [quantity, setQuantity] = useState(1)
  const [added, setAdded] = useState(false)
  const { addItem, count } = useCart()

  if (!product) {
    return (
      <section className="bg-[#0A0F0B] px-6 py-28 text-center">
        <h1 className="font-display text-5xl font-black text-brand-light">
          PRODUCT LINE NOT FOUND
        </h1>
        <Link
          to="/shop"
          className="mt-6 inline-block text-brand-green-light hover:underline"
        >
          Return to shop
        </Link>
      </section>
    )
  }

  const selectedItem =
    product.items.find((item) => item.id === selectedId) ?? product.items[0]

  const handleAdd = () => {
    addItem({
      productId: product.id,
      productName: product.name,
      itemId: selectedItem.id,
      itemName: selectedItem.name,
      price: selectedItem.price,
      image: product.image,
      quantity,
    })
    setAdded(true)
  }

  return (
    <div className="bg-[#0A0F0B]">
      <Seo
        title={`${product.name} | Buy in Uganda & USA | Jero-One`.slice(0, 70)}
        description={product.description.slice(0, 158)}
        path={`/shop/${product.id}`}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ProductGroup",
              name: product.name,
              description: product.description,
              image: `https://www.jero-onetechnologies.com${product.image}`,
              url: `https://www.jero-onetechnologies.com/shop/${product.id}`,
              hasVariant: product.items.map((it) => ({
                "@type": "Product",
                name: it.name,
                sku: `${product.id}:${it.id}`,
                description: it.detail,
                ...(it.brand ? { brand: { "@type": "Brand", name: it.brand } } : {}),
                ...(it.quoteOnly ? {} : { offers: { "@type": "Offer", price: it.price, priceCurrency: "USD", availability: "https://schema.org/InStock", seller: { "@type": "Organization", name: "Jero-One Technologies Inc." } } }),
              })),
            },
            ...(product.faqs ? [{ "@type": "FAQPage", mainEntity: product.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }] : []),
          ],
        }}
      />
      <section className="mx-auto max-w-7xl px-6 py-10 md:px-14 md:py-16">
        <Link
          to="/shop"
          className="text-sm font-semibold text-brand-green-light hover:underline"
        >
          ← Back to all product lines
        </Link>

        <div className="mt-8 grid overflow-hidden rounded-3xl border border-brand-green/15 bg-brand-mid lg:grid-cols-2">
          <div className="relative min-h-96 overflow-hidden bg-brand-navy">
            <img
              src={product.image}
              alt={product.name}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 to-transparent" />
            <span className="absolute left-6 top-6 rounded-full bg-brand-navy/90 px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-green-light">
              {product.condition}
            </span>
          </div>

          <div className="p-7 md:p-12">
            <div className="font-mono text-xs tracking-widest text-brand-green-light">
              {product.category}
            </div>
            <h1 className="mt-3 font-display text-5xl font-black leading-none text-brand-light">
              {product.name}
            </h1>
            <p className="mt-5 leading-relaxed text-brand-muted">
              {product.description}
            </p>

            <div className="mt-9">
              <div className="mb-3 text-sm font-bold text-brand-light">
                Select an item
              </div>
              <div className="space-y-3" data-testid="variant-list">
                {product.items.map((item) => (
                  <label
                    key={item.id}
                    data-testid={`variant-${item.id}`}
                    className={`flex cursor-pointer items-center justify-between gap-5 rounded-xl border p-4 transition-colors ${
                      selectedItem.id === item.id
                        ? "border-brand-green bg-brand-green/10"
                        : "border-brand-green/15 bg-brand-navy hover:border-brand-green/40"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="catalog-item"
                        value={item.id}
                        checked={selectedItem.id === item.id}
                        onChange={() => {
                          setSelectedId(item.id)
                          setAdded(false)
                        }}
                        className="accent-brand-green"
                      />
                      <span>
                        <span className="block font-semibold text-brand-light">
                          {item.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-brand-muted">
                          {item.detail}
                        </span>
                      </span>
                    </span>
                    <span className="shrink-0 font-display text-2xl font-black text-brand-light">
                      {item.quoteOnly ? <span className="text-base text-brand-green-light">Price on request</span> : currency.format(item.price)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-end gap-3">
              <label>
                <span className="mb-2 block text-xs font-semibold text-brand-muted">
                  Quantity
                </span>
                <input
                  type="number"
                  min="1"
                  max="9999"
                  value={quantity}
                  data-testid="shopline-qty-input"
                  onChange={(event) =>
                    setQuantity(Math.max(1, Number(event.target.value)))
                  }
                  className="w-24 rounded-xl border border-brand-green/20 bg-brand-navy px-4 py-3 text-brand-light outline-none focus:border-brand-green"
                />
              </label>
              {!selectedItem.quoteOnly && (
                <button
                  type="button"
                  onClick={handleAdd}
                  data-testid="shopline-add-to-cart"
                  className="rounded-xl bg-brand-green px-7 py-3 font-bold text-brand-light transition-colors hover:bg-brand-green-light"
                >
                  {added ? "Added to cart" : "Add to cart"}
                </button>
              )}
              <Link
                to="/checkout"
                className="rounded-xl border border-brand-green/30 px-7 py-3 font-bold text-brand-light transition-colors hover:border-brand-green"
              >
                View cart ({count})
              </Link>
              <QuoteButton items={[{ name: `${product.name} — ${selectedItem.name}`, quantity, sku: `${product.id}:${selectedItem.id}` }]} className={selectedItem.quoteOnly ? "!border-brand-green bg-brand-green hover:!bg-brand-green-light" : ""} />
            </div>
            {product.priceNote && (
              <p className="mt-4 text-xs text-brand-muted" data-testid="price-note">{product.priceNote}</p>
            )}
            {selectedItem.quoteOnly && (
              <p className="mt-5 rounded-xl border border-brand-green/25 bg-brand-navy p-4 text-sm text-brand-muted" data-testid="quote-only-note">
                Pricing depends on licensing, number of sites and installation scope. Request a quote and our engineers will size the right solution, usually within one business day.
              </p>
            )}
            {isDiscountable(product.id) && !selectedItem.quoteOnly && (
              <div className="mt-6 rounded-xl border border-brand-green/20 bg-brand-navy p-4" data-testid="bulk-pricing">
                <div className="text-xs font-bold uppercase tracking-widest text-brand-green-light">Bulk pricing</div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-brand-muted">
                  {BULK_TIERS.map((t) => (
                    <span key={t.min}><b className="text-brand-light">{t.min}+ units</b> · {t.rate * 100}% off ({currency.format(selectedItem.price * (1 - t.rate))} each)</span>
                  ))}
                </div>
                <div className="mt-3 text-sm text-brand-light" data-testid="line-estimate">
                  {quantity} × {currency.format(selectedItem.price)} = <b>{currency.format(lineTotal(product.id, selectedItem.price, quantity))}</b>
                  {discountRate(product.id, quantity) > 0 && <span className="ml-2 text-brand-green-light">({discountRate(product.id, quantity) * 100}% bulk discount applied)</span>}
                </div>
              </div>
            )}
            <p className="mt-4 text-xs text-brand-muted">
              Taxes, delivery, installation, and configuration are not included
              unless stated.
            </p>
          </div>
        </div>
      </section>
      <ProductExtras product={product} />
    </div>
  )
}
